<script setup>
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { computed, reactive, watch } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/20/solid';
import { CalendarIcon } from '@heroicons/vue/24/outline';
import { useGetLocalizedDateTime } from '@/Composables/localizedDateTime.js';
import JetListBox from '@/Components/FormListBox.vue';

const page = usePage();
const locale = computed(() => page.props.locale);

const props = defineProps({
    businessAreas: {
        type: Array,
        required: true,
    },
    topics: {
        type: Array,
        required: true,
    },
    collectionTypes: {
        type: Array,
        required: true,
    },
    formDates: {
        type: Array,
        default: null,
    },
    formBusinessAreas: {
        type: Array,
        required: true,
    },
    formTopics: {
        type: Array,
        required: true,
    },
    formCollectionTypes: {
        type: Array,
        required: true,
    },
    formCanBeSent: {
        type: Boolean,
        required: true,
    },
    formCanBeReset: {
        type: Boolean,
        required: true,
    },
    formIsProcessing: {
        type: Boolean,
        required: true,
    },
});

const emit = defineEmits([
    'update:dates',
    'update:business-areas',
    'update:topics',
    'update:collection-types',
    'update:publication-categories',
]);

const form = reactive({
    dates: props.formDates,
    businessAreas: props.formBusinessAreas,
    topics: props.formTopics,
    collectionTypes: props.formCollectionTypes,
});

watch(form, value => {
    for (const [k, v] of Object.entries(value)) {
        emit(`update:${toKebabCase(k)}`, v);
    }
});

const resetDates = () => {
    form.dates = null;
};

const dateFormat = dates => {
    if (dates[1] === undefined || dates[0].toISOString() === dates[1].toISOString()) {
        return useGetLocalizedDateTime(dates[0].toISOString(), 'P');
    }

    return `${useGetLocalizedDateTime(dates[0].toISOString(), 'P')} - ${useGetLocalizedDateTime(dates[1].toISOString(), 'P')}`;
};

const toKebabCase = string_ => string_.replaceAll(/[A-Z]+(?![a-z])|[A-Z]/g, ($, ofs) => (ofs ? '-' : '') + $.toLowerCase());
</script>

<template>
    <div class="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-6 lg:grow">
        <VueDatePicker
            v-model="form.dates"
            :config="{ keepActionRow: true }"
            :alt-position="() => ({ top: 'auto', left: 'auto' })"
            :clearable="false"
            :enable-time-picker="false"
            :format="dateFormat"
            :locale
            :max-date="new Date()"
            :month-change-on-scroll="false"
            :placeholder="$t('articles.search.filters.dates.placeholder')"
            :transitions="{ menuAppearTop: 'fade', menuAppearBottom: 'fade' }"
            auto-apply
            model-type="timestamp"
            range
            name="dates"
            class="z-[5]"
        >
            <template #action-preview />
            <template #action-buttons>
                <button
                    type="button"
                    @click="resetDates"
                >
                    {{ $t('articles.search.filters.dates.reset') }}
                </button>
            </template>
            <template #input-icon>
                <CalendarIcon
                    aria-hidden="true"
                    class="w-4 h-4 text-gray-500 [&>path]:stroke-[2]"
                />
            </template>
            <template #arrow-left>
                <ChevronLeftIcon
                    aria-hidden="true"
                    class="w-5 h-5 text-gray-700"
                />
            </template>
            <template #arrow-right>
                <ChevronRightIcon
                    aria-hidden="true"
                    class="w-5 h-5 text-gray-700"
                />
            </template>
        </VueDatePicker>
        <JetListBox
            v-model="form.businessAreas"
            :options="businessAreas.map(area => ({ value: area.id, text: area.name }))"
            :placeholder="$t('articles.search.filters.businessAreas.placeholder')"
            :placeholder-is-clickable="true"
        />
        <JetListBox
            v-model="form.topics"
            :options="topics.map(topic => ({ value: topic.id, text: topic.name }))"
            :placeholder="$t('articles.search.filters.topics.placeholder')"
            :placeholder-is-clickable="true"
        />
        <JetListBox
            v-model="form.collectionTypes"
            :options="collectionTypes.map(type => ({
                value: type.id,
                text: type.name,
                options: type.options?.map(sub => ({ value: sub.id, text: sub.name })),
            }))"
            :placeholder="$t('articles.search.filters.collectionTypes.placeholder')"
            :placeholder-is-clickable="true"
        />
    </div>
</template>

<style>
.dp--menu-wrapper {
    @apply w-full;
}

.fade-leave-active {
    @apply transition duration-100 ease-in;
}

.fade-leave-from {
    @apply opacity-100;
}

.fade-leave-to {
    @apply opacity-0;
}

.dp__cell_auto_range_start,
.dp__date_hover_start:hover,
.dp__range_start,
.dp__cell_auto_range_end,
.dp__date_hover_end:hover,
.dp__range_end,
.dp__pointer.dp__today.dp__range_between {
    @apply rounded-full;
}

.dp__calendar_item:hover > .dp__cell_inner:not(.dp__range_end):not(.dp__range_start) {
    @apply rounded-full bg-deep-teal-200;
}

.dp__calendar_item:has(.dp__range_start),
.dp__calendar_item:hover:has(.dp__date_hover_start) {
    background: linear-gradient(-90deg, theme('colors.deep-teal.200') 50%, transparent 51%);
}

.dp__calendar_item:has(.dp__range_end),
.dp__calendar_item:hover:has(.dp__date_hover_end) {
    background: linear-gradient(-90deg, transparent 50%, theme('colors.deep-teal.200') 51%);
}

.dp__calendar_item:has(.dp__range_start):has(.dp__range_end) {
    @apply bg-none;
}

.dp__calendar_item:has(.dp__range_between),
.dp__calendar_item:hover > .dp__date_hover_end,
.dp__calendar_item:hover > .dp__date_hover_start {
    @apply bg-deep-teal-200;
}

.dp__menu {
    @apply w-full mt-1.5 ring-1 ring-gray-500 ring-opacity-5 shadow-lg;

    .dp__btn {
        @apply text-base;
    }
}

.dp__input {
    @apply text-base font-normal leading-6 transition-none;

    &:focus {
        @apply ring-1 ring-teal-700 border-deep-teal-600;
    }
}

.dp__input_focus {
    @apply ring-1 ring-teal-700;
}

.dp__input_icon {
    @apply left-auto right-3;
}

.dp__calendar_header_separator {
    @apply h-0;
}

.dp__arrow_top {
    @apply hidden;
}

.dp__action_buttons {
    @apply mx-auto flex-initial text-deep-teal-700 text-base font-semibold hover:underline hover:text-deep-teal-900 mb-2;
}

.dp__theme_light {
    .dp__calendar_header {
        @apply text-gray-400;
    }

    @apply font-bold;

    --dp-background-color: theme('colors.white');
    --dp-text-color: theme('colors.gray.800');
    --dp-hover-color: theme('colors.deep-teal.200');
    --dp-hover-text-color: theme('colors.gray.800');
    --dp-hover-icon-color: theme('colors.gray.800');
    --dp-primary-color: theme('colors.deep-teal.700');
    --dp-primary-text-color: theme('colors.white');
    --dp-secondary-color: theme('colors.gray.400');
    --dp-border-color: theme('colors.gray.300');
    --dp-menu-border-color: theme('colors.transparent');
    --dp-border-color-hover: theme('colors.gray.300');
    --dp-disabled-color: theme('colors.gray.100');
    --dp-scroll-bar-background: theme('colors.gray.50');
    --dp-scroll-bar-color: theme('colors.gray.500');
    --dp-success-color: #76d275;
    --dp-success-color-disabled: #a3d9b1;
    --dp-danger-color: #ff6f60;
    --dp-icon-color: theme('colors.gray.800');
    --dp-highlight-color: theme('colors.deep-teal.200');
}

:root {
    --dp-font-family: theme('fontFamily.sans');
    --dp-border-radius: theme('borderRadius.md');
    --dp-cell-border-radius: theme('borderRadius.full');
    --dp-common-transition: all 0.1s theme('transitionTimingFunction.in');

    --dp-button-heigh: theme('height.8');
    --dp-month-year-row-height: theme('height.8');
    --dp-month-year-row-button-size: theme('height.8');
    --dp-button-icon-height: theme('height.5');
    --dp-cell-size: theme('width.8');
    --dp-cell-padding: theme('spacing.2');
    --dp-common-padding: theme('spacing.1');
    --dp-action-row-padding: theme('spacing.1');
    --dp-input-icon-padding: theme('spacing.3');
    --dp-input-padding: theme('spacing.2') theme('spacing.9') theme('spacing.2') theme('spacing.3');
    --dp-menu-min-width: theme('width.16');
    --dp-action-buttons-padding: theme('spacing.1') theme('spacing[1.5]');
    --dp-row-margin: theme('spacing[1.5]') 0;
    --dp-calendar-header-cell-padding: theme('spacing.2');
    --dp-two-calendars-spacing: theme('spacing.3');
    --dp-overlay-col-padding: theme('spacing.1');
    --dp-time-inc-dec-button-size: theme('spacing.8');
    --dp-menu-padding: theme('spacing[1.5]') theme('spacing.2');

    --dp-font-size: theme('fontSize.sm');
    --dp-preview-font-size: theme('fontSize.sm');
    --dp-time-font-size: theme('fontSize.sm');

    --dp-animation-duration: theme('transitionDuration.100');
    --dp-menu-appear-transition-timing: theme('transitionTimingFunction.in');
    --dp-transition-timing: theme('transitionTimingFunction.DEFAULT');
}
</style>
